import { STAFF_ATTENDANCE_MARKS } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, primaryButton, selectClass } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

interface Row {
  staffId: string;
  name: string;
  employeeNumber: string;
  status: string | null;
  onApprovedLeave: boolean;
}
interface Register {
  date: string;
  schoolDay: boolean;
  nonSchoolReason: string | null;
  staff: Row[];
}

export function AttendanceTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [date, setDate] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const key = ['hr', schoolId, 'attendance', date];

  const register = useQuery({
    queryKey: key,
    queryFn: () => api<Register>(`/schools/${schoolId}/staff-attendance/${date}`),
    enabled: !!date,
  });
  useEffect(() => {
    if (register.data)
      setDraft(
        Object.fromEntries(register.data.staff.map((s) => [s.staffId, s.status ?? 'present'])),
      );
  }, [register.data]);

  const save = useMutation({
    mutationFn: (records: object[]) =>
      api<Register>(`/schools/${schoolId}/staff-attendance/${date}`, {
        method: 'PUT',
        body: { records },
      }),
    onSuccess: (saved) => {
      setMessage({ kind: 'ok', text: t('hr.saved') });
      queryClient.setQueryData(key, saved);
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  const data = register.data;
  const editable = !!data?.schoolDay && can('staff_attendance.mark', schoolId);

  return (
    <section aria-label={t('hr.tab.attendance')} className="space-y-4">
      <TextField
        label={t('hr.date')}
        type="date"
        value={date}
        max={new Date().toLocaleDateString('en-CA')}
        onChange={(e) => setDate(e.target.value)}
      />
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {register.isError && <Notice kind="error">{errorMessage(register.error)}</Notice>}
      {data && !data.schoolDay && (
        <Notice kind="error">{data.nonSchoolReason ?? t('hr.notSchoolDay')}</Notice>
      )}
      {data?.staff.length === 0 && <p className="text-slate-600">{t('hr.noStaff')}</p>}
      {data && data.staff.length > 0 && (
        <>
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-600">
              <tr>
                <th scope="col" className="p-2">
                  {t('hr.staff')}
                </th>
                <th scope="col" className="p-2">
                  {t('hr.status')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.staff.map((s) => (
                <tr key={s.staffId}>
                  <th scope="row" className="p-2 font-medium">
                    {s.name}{' '}
                    <span className="font-mono text-xs text-slate-500">{s.employeeNumber}</span>
                  </th>
                  <td className="p-2">
                    {s.onApprovedLeave ? (
                      <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-900">
                        {t('hr.onLeave')}
                      </span>
                    ) : (
                      <select
                        aria-label={t('hr.statusFor', { name: s.name })}
                        disabled={!editable}
                        value={draft[s.staffId] ?? 'present'}
                        onChange={(e) => setDraft({ ...draft, [s.staffId]: e.target.value })}
                        className={selectClass}
                      >
                        {STAFF_ATTENDANCE_MARKS.map((m) => (
                          <option key={m} value={m}>
                            {t(`hr.status.${m}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {editable && (
            <button
              type="button"
              className={primaryButton}
              disabled={save.isPending}
              onClick={() => {
                setMessage(null);
                save.mutate(
                  data.staff
                    .filter((s) => !s.onApprovedLeave)
                    .map((s) => ({ staffId: s.staffId, status: draft[s.staffId] ?? 'present' })),
                );
              }}
            >
              {t('hr.saveAttendance')}
            </button>
          )}
        </>
      )}
    </section>
  );
}
